import { Metadata } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { BlogPostContent } from '../../../../components/Blog/BlogPost';
import { BlogPostHero } from '../../../../components/Blog/BlogPostHero';
import BasicPage from '../../../../components/Layouts/BasicPage';
import { BlogPost } from '../../../../lib/blog';
import { getAllBlogPosts, getBlogPostBySlug } from '../../../../lib/contentful';
import { Companies } from '../../../../util/CompanyList';
import { Routes } from '../../../../util/routes';
import { withOnboardingRequired } from '../../../../util/withOnboardingRequired';

type Props = {
  post: BlogPost;
  host: string;
};

export const BlogPostPage = ({ post }: Props) => {
  const router = useRouter();

  /**
   * If the post has been removed this should route back
   * to the Blog homepage instead of throwing a 404 error
   */
  useEffect(() => {
    if (!post) {
      router.push(Routes.blog(router.query['company-slug'] as string));
    }
  }, [router, post]);

  if (!post) {
    return null;
  }

  return (
    <BasicPage className="blog-page">
      <Head>
        <title>{post.title}</title>
      </Head>

      <div className="content">
        <BlogPostHero post={post} />
        <BlogPostContent post={post} />
      </div>
    </BasicPage>
  );
};

export async function getStaticPaths() {
  const blogPosts = await getAllBlogPosts();
  const paths = blogPosts?.flatMap(({ slug }) =>
    Companies.flatMap(({ slug: companySlug }) =>
      Routes.blogPost(slug, companySlug)
    )
  );

  return {
    paths: paths ?? [],
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }) {
  const blogPost = await getBlogPostBySlug(params.slug);
  const metadata: Metadata = {
    openGraph: {
      title: blogPost?.title,
      description: `${blogPost.title} - ${blogPost.author?.name}`,
      images: blogPost.thumbnail?.url,
    },
  };

  return {
    props: {
      post: blogPost ?? null,
      metadata,
    },
    revalidate: 1,
  };
}

export default withOnboardingRequired(BlogPostPage);
